import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Check, Crown, Loader2, Sparkles, X, Tag } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import PageHeader from '../components/PageHeader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const money = (n) => `$${Number(n || 0).toFixed(2)}`;
const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' }); }
  catch { return '—'; }
};

export default function Membership() {
  const token = localStorage.getItem('session_token');
  const authHeader = { headers: { Authorization: `Bearer ${token}` } };
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [current, setCurrent] = useState(null);
  const [usage, setUsage] = useState(null);
  const [me, setMe] = useState(null);
  const [billing, setBilling] = useState('yearly');
  const [verifying, setVerifying] = useState(false);
  const [checkout, setCheckout] = useState(null); // { plan }
  const [billingHistory, setBillingHistory] = useState(null);
  const autoStarted = useRef(false);

  const loadStatus = async () => {
    const res = await axios.get(`${BACKEND_URL}/api/membership/status`, authHeader);
    setCurrent(res.data);
    axios.get(`${BACKEND_URL}/api/membership/usage`, authHeader).then((r) => setUsage(r.data)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/membership/billing-history`, authHeader).then((r) => setBillingHistory(r.data)).catch(() => {});
    return res.data;
  };

  useEffect(() => {
    axios.get(`${BACKEND_URL}/api/membership/plans`).then((r) => setPlans(r.data.plans)).catch(() => {});
    axios.get(`${BACKEND_URL}/api/auth/me`, authHeader).then((r) => setMe(r.data)).catch(() => {});
    loadStatus().catch(() => {});
  }, []); // eslint-disable-line

  const pollStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 6) { setVerifying(false); toast.error('Still processing your payment. It may take a moment to reflect.'); return; }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/membership/checkout/status/${sessionId}`, authHeader);
      if (res.data.payment_status === 'paid') { setVerifying(false); toast.success('🎉 Welcome! Your membership is now active.'); await loadStatus(); return; }
      if (res.data.status === 'expired') { setVerifying(false); toast.error('Payment session expired. Please try again.'); return; }
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch { setVerifying(false); toast.error('Could not verify payment.'); }
  };

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) { setVerifying(true); pollStatus(sessionId); searchParams.delete('session_id'); setSearchParams(searchParams, { replace: true }); }
  }, []); // eslint-disable-line

  const switchToFree = async () => {
    try {
      const res = await axios.post(`${BACKEND_URL}/api/membership/checkout`, { plan_id: 'free', origin_url: window.location.origin }, authHeader);
      toast.success(res.data.message); await loadStatus();
    } catch { toast.error('Could not switch plan.'); }
  };

  useEffect(() => {
    const wantPlan = searchParams.get('plan');
    if (!autoStarted.current && wantPlan && current && plans.length && !searchParams.get('session_id')) {
      autoStarted.current = true;
      searchParams.delete('plan'); setSearchParams(searchParams, { replace: true });
      const p = plans.find((x) => x.id === wantPlan);
      if (p && wantPlan !== 'free' && current.plan !== wantPlan) setCheckout({ plan: p });
    }
  }, [current, plans]); // eslint-disable-line

  useEffect(() => {
    const gcode = searchParams.get('group') || localStorage.getItem('group_code');
    if (autoStarted.current || !gcode || !plans.length || !current || searchParams.get('session_id')) return;
    autoStarted.current = true;
    axios.get(`${BACKEND_URL}/api/membership/group/${gcode}`).then((r) => {
      const g = r.data;
      localStorage.removeItem('group_code');
      searchParams.delete('group'); setSearchParams(searchParams, { replace: true });
      if (!g.active) return;
      if (g.applies_to_billing_cycle !== 'all') setBilling(g.applies_to_billing_cycle);
      const pid = g.applies_to_plan === 'all' ? 'premium' : g.applies_to_plan;
      const p = plans.find((x) => x.id === pid);
      if (p) setCheckout({ plan: p, initialCode: g.code });
    }).catch(() => { localStorage.removeItem('group_code'); });
  }, [current, plans]); // eslint-disable-line

  const cancel = async () => {
    if (!window.confirm("Cancel auto-renewal? You'll keep full access until the end of your current billing period.")) return;
    try {
      const res = await axios.post(`${BACKEND_URL}/api/membership/cancel`, {}, authHeader);
      toast.success(res.data?.message || 'Auto-renewal cancelled.');
      await loadStatus();
    } catch { toast.error('Could not cancel.'); }
  };

  const currentPlan = current?.plan || 'free';
  const priceFor = (p) => (p.id === 'free' ? 0 : (billing === 'yearly' ? p.yearly_price : p.monthly_price));

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid="membership-page">
      <PageHeader title="Membership" user={me}
        titleIcon={<div className="w-9 h-9 rounded-xl bg-[#F7F1E8] flex items-center justify-center"><Crown className="h-5 w-5 text-[#C8A97E]" /></div>} />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        <div className="text-center max-w-2xl mx-auto mb-6">
          <h2 className="font-display text-3xl sm:text-4xl font-bold text-[#1C252E]">Choose Your Plan</h2>
          <p className="mt-3 text-slate-600">Start free and upgrade whenever you're ready. Cancel anytime.</p>
        </div>

        {/* Billing toggle */}
        <div className="flex flex-col items-center gap-2 mb-8">
          <div className="inline-flex items-center bg-white border border-slate-200 rounded-full p-1 shadow-sm" data-testid="billing-toggle">
            {[['monthly', 'Monthly'], ['yearly', 'Yearly']].map(([v, label]) => (
              <button key={v} onClick={() => setBilling(v)} data-testid={`billing-${v}`}
                className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${billing === v ? 'bg-[#2B4C7E] text-white' : 'text-slate-600'}`}>
                {label}{v === 'yearly' && <span className="ml-1.5 text-xs font-bold" style={{ color: billing === 'yearly' ? '#F4D9A0' : '#C8A97E' }}>Save up to 17%</span>}
              </button>
            ))}
          </div>
          {billing === 'yearly' && <p className="text-sm text-[#7FA98C] font-medium" data-testid="yearly-note">Best value — save when you choose yearly billing.</p>}
        </div>

        <div className="flex items-center justify-center gap-2 mb-8">
          <span className="text-sm text-slate-500">Current plan:</span>
          <span className="inline-flex items-center gap-1.5 bg-[#EBF0F5] text-[#2B4C7E] font-semibold text-sm px-3 py-1 rounded-full" data-testid="current-plan-badge">
            <Sparkles className="h-3.5 w-3.5 text-[#C8A97E]" />{current?.details?.name || 'Free Plan'}
          </span>
          {verifying && <span className="inline-flex items-center gap-1.5 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> Verifying payment…</span>}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {plans.map((p) => {
            const isCurrent = currentPlan === p.id;
            const popular = p.id === 'premium';
            const price = priceFor(p);
            return (
              <motion.div key={p.id} whileHover={{ y: -6 }} className={`relative bg-white rounded-3xl p-8 border flex flex-col shadow-[0_8px_30px_rgb(0,0,0,0.04)] ${popular ? 'border-2 border-[#C8A97E]' : 'border-slate-100'}`} data-testid={`plan-card-${p.id}`}>
                {popular && <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#C8A97E] text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">Most Popular</span>}
                {p.id !== 'free' && billing === 'yearly' && <span className="absolute -top-3 right-4 bg-[#7FA98C] text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-testid={`best-value-${p.id}`}>Best Value</span>}
                <h3 className="font-display text-xl font-bold text-[#1C252E]">{p.name}</h3>
                <p className="text-sm text-slate-500 mt-1">{p.tagline}</p>
                <div className="mt-4 flex items-end gap-1">
                  <span className="font-display text-4xl font-bold text-[#2B4C7E]" data-testid={`price-${p.id}`}>{money(price)}</span>
                  <span className="text-slate-500 mb-1">/{p.id === 'free' ? 'month' : (billing === 'yearly' ? 'year' : 'month')}</span>
                </div>
                {p.id !== 'free' && billing === 'yearly' && (
                  <p className="mt-1 text-xs text-slate-500" data-testid={`savings-${p.id}`}>
                    <span className="line-through">{money(p.monthly_price * 12)}/year</span> · <span className="text-[#7FA98C] font-semibold">Save {money(p.yearly_savings)}</span>
                  </p>
                )}
                <ul className="mt-6 space-y-3 flex-1">
                  {p.features.map((f) => (<li key={f} className="flex items-start gap-3 text-sm text-slate-700"><Check className="h-5 w-5 text-[#2B4C7E] shrink-0" /> {f}</li>))}
                </ul>
                {isCurrent ? (
                  <div className="mt-8 space-y-2">
                    <div className="w-full text-center rounded-full bg-[#EBF0F5] text-[#2B4C7E] font-semibold py-3" data-testid={`plan-current-${p.id}`}>Your current plan</div>
                    {p.id !== 'free' && (current?.cancel_at_period_end
                      ? <p className="w-full text-center text-xs text-amber-600" data-testid="cancel-scheduled">Won't renew · access until {fmtDate(current?.plan_expires_at)}</p>
                      : <button onClick={cancel} className="w-full text-xs text-slate-400 hover:text-red-500" data-testid="cancel-membership">Cancel auto-renewal</button>)}
                  </div>
                ) : (
                  <Button onClick={() => (p.id === 'free' ? switchToFree() : setCheckout({ plan: p }))} data-testid={`plan-cta-${p.id}`}
                    className={`mt-8 w-full rounded-full font-semibold py-6 ${popular ? 'bg-[#C8A97E] hover:bg-[#B3956B]' : 'bg-[#2B4C7E] hover:bg-[#1E3A63]'} text-white`}>
                    {p.id === 'free' ? 'Switch to Free' : `Get ${p.name.replace(' Plan', '')}`}
                  </Button>
                )}
              </motion.div>
            );
          })}
        </div>
        <p className="text-center text-xs text-slate-400 mt-8">Choose yearly billing and save while protecting your family's legacy. Payments securely processed by Stripe.</p>

        {/* Billing history */}
        <div className="mt-14 max-w-3xl mx-auto" data-testid="billing-history-panel">
          <h3 className="font-display text-2xl font-bold text-[#1C252E] mb-4">Billing &amp; history</h3>
          <div className="bg-white rounded-3xl border border-slate-100 shadow-[0_8px_30px_rgb(0,0,0,0.04)] overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-slate-100">
              <div className="bg-white p-5">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">Current plan</p>
                <p className="text-lg font-bold text-[#2B4C7E] mt-1" data-testid="billing-current-plan">{billingHistory?.plan_name || current?.details?.name || 'Free Plan'}</p>
                {billingHistory?.billing_cycle && <p className="text-xs text-slate-500 mt-0.5 capitalize">{billingHistory.billing_cycle} billing</p>}
              </div>
              <div className="bg-white p-5">
                <p className="text-xs font-medium text-slate-400 uppercase tracking-wide">{billingHistory?.is_paid ? (billingHistory?.cancel_at_period_end ? 'Access until (won\'t renew)' : 'Renews on') : 'Status'}</p>
                <p className="text-lg font-bold text-[#1C252E] mt-1" data-testid="billing-renews-on">
                  {billingHistory?.is_paid ? fmtDate(billingHistory?.renews_on) : 'Free — no billing'}
                </p>
              </div>
            </div>

            <div className="border-t border-slate-100">
              {billingHistory === null ? (
                <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-[#2B4C7E]" /></div>
              ) : (billingHistory.history || []).length === 0 ? (
                <p className="text-center text-sm text-slate-400 py-10" data-testid="billing-empty">No payments yet. Your purchases will appear here.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-slate-400 border-b border-slate-100">
                        <th className="px-5 py-3 font-medium">Date</th>
                        <th className="px-5 py-3 font-medium">Item</th>
                        <th className="px-5 py-3 font-medium">Type</th>
                        <th className="px-5 py-3 font-medium text-right">Amount</th>
                        <th className="px-5 py-3 font-medium text-right">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billingHistory.history.map((h, i) => (
                        <tr key={i} className="border-b border-slate-50" data-testid={`billing-row-${i}`}>
                          <td className="px-5 py-3 whitespace-nowrap text-slate-600">{fmtDate(h.date)}</td>
                          <td className="px-5 py-3 text-[#1C252E] font-medium">
                            {h.description}
                            {h.discount_code && <span className="ml-2 text-xs text-[#7FA98C]">({h.discount_code})</span>}
                          </td>
                          <td className="px-5 py-3"><span className="text-xs px-2 py-0.5 rounded-full bg-[#EBF0F5] text-[#2B4C7E]">{h.kind}</span></td>
                          <td className="px-5 py-3 text-right font-semibold text-[#1C252E] whitespace-nowrap">{money(h.amount)}</td>
                          <td className="px-5 py-3 text-right"><span className="text-xs px-2 py-0.5 rounded-full bg-[#EAF5EE] text-[#5E8C6E] capitalize">Paid</span></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {checkout && <CheckoutModal plan={checkout.plan} billing={billing} initialCode={checkout.initialCode} authHeader={authHeader} onClose={() => setCheckout(null)} />}
    </div>
  );
}

function CheckoutModal({ plan, billing, initialCode, authHeader, onClose }) {
  const base = billing === 'yearly' ? plan.yearly_price : plan.monthly_price;
  const [code, setCode] = useState(initialCode || '');
  const [applying, setApplying] = useState(false);
  const [disc, setDisc] = useState(null); // { discount_amount, total, code }
  const [discErr, setDiscErr] = useState('');
  const [agree, setAgree] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const total = disc ? disc.total : base;

  const apply = async (codeArg) => {
    const c = (codeArg ?? code).trim();
    if (!c) return;
    setApplying(true); setDiscErr('');
    try {
      const r = await axios.post(`${BACKEND_URL}/api/membership/validate-discount`,
        { code: c, plan_id: plan.id, billing_cycle: billing }, authHeader);
      setDisc(r.data);
      toast.success('Discount applied!');
    } catch (e) {
      setDisc(null);
      setDiscErr(e?.response?.data?.detail || 'Could not apply code');
    } finally { setApplying(false); }
  };

  useEffect(() => { if (initialCode) apply(initialCode); }, []); // eslint-disable-line

  const subscribe = async () => {
    if (!agree) { toast.error('Please agree to the terms to continue.'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/membership/checkout`,
        { plan_id: plan.id, billing_cycle: billing, discount_code: disc ? disc.code : undefined, origin_url: window.location.origin }, authHeader);
      if (res.data.free || res.data.promotional) { toast.success(res.data.message); window.location.href = '/membership'; return; }
      if (res.data.url) { window.location.href = res.data.url; return; }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not start checkout.'); setSubmitting(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4" data-testid="checkout-modal">
      <div className="bg-white rounded-3xl w-full max-w-md p-6 sm:p-7 max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-xl font-bold text-[#1C252E]">Checkout</h3>
          <button onClick={onClose} data-testid="checkout-close"><X className="h-5 w-5 text-slate-400" /></button>
        </div>

        <div className="rounded-2xl bg-[#FDFBF7] border border-slate-100 p-4 space-y-1.5 text-sm">
          <div className="flex justify-between"><span className="text-slate-500">Plan</span><span className="font-semibold text-[#1C252E]">{plan.name}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Billing</span><span className="font-semibold capitalize text-[#1C252E]">{billing}</span></div>
          <div className="flex justify-between"><span className="text-slate-500">Original price</span><span className="text-[#1C252E]" data-testid="checkout-subtotal">{money(base)}</span></div>
          {disc && <div className="flex justify-between text-[#7FA98C] font-semibold"><span>Discount ({disc.code})</span><span data-testid="checkout-discount">-{money(disc.discount_amount)}</span></div>}
          <div className="flex justify-between border-t border-slate-200 pt-1.5 mt-1.5 text-base"><span className="font-bold text-[#1C252E]">Total today</span><span className="font-bold text-[#2B4C7E]" data-testid="checkout-total">{money(total)}</span></div>
        </div>

        {/* Discount code */}
        <div className="mt-4">
          <label className="block text-xs font-semibold text-slate-600 mb-1">Discount Code</label>
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Tag className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input value={code} onChange={(e) => { setCode(e.target.value.toUpperCase()); }} placeholder="Enter code"
                className="w-full h-11 pl-9 pr-3 rounded-xl border border-slate-200 text-sm outline-none focus:border-[#2B4C7E] uppercase" data-testid="checkout-discount-input" />
            </div>
            <button onClick={() => apply()} disabled={applying || !code.trim()} className="h-11 px-5 rounded-xl bg-[#2B4C7E] text-white text-sm font-semibold disabled:opacity-50" data-testid="checkout-apply-discount">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Apply'}
            </button>
          </div>
          {discErr && <p className="text-xs text-red-600 mt-1" data-testid="checkout-discount-error">{discErr}</p>}
        </div>

        <p className="mt-4 text-xs text-slate-500">
          Renews at {money(base)}/{billing === 'yearly' ? 'year' : 'month'}. Discounts may apply only to the first billing cycle unless stated otherwise.
        </p>
        <label className="mt-3 flex items-start gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={agree} onChange={(e) => setAgree(e.target.checked)} className="mt-1" data-testid="checkout-agree" />
          <span>I agree to the <a href="/terms" target="_blank" rel="noreferrer" className="text-[#2B4C7E] font-semibold underline">Terms</a> and understand billing.</span>
        </label>
        <Button onClick={subscribe} disabled={submitting} data-testid="checkout-subscribe"
          className="mt-4 w-full rounded-full font-semibold py-6 bg-[#C8A97E] hover:bg-[#B3956B] text-white">
          {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : `Subscribe — ${money(total)}`}
        </Button>
      </div>
    </div>
  );
}
